import {
	GuardedType,
	IsInterface,
	isArray,
	isBoolean,
	isNullable,
	isSingletonStringUnion,
	isString
} from "generic-type-guard";

const isStringOrDate = (v: unknown): v is string | Date => typeof v === "string" || v instanceof Date;

export const isInvitationDto = new IsInterface()
	.withProperties({
		tokenHash: isString,
		email: isString,
		status: isSingletonStringUnion("ACTIVE", "USED", "REVOKED", "EXPIRED"),
		expiresAt: isString,
		createdAt: isString,
		createdBy: isString
	})
	.withOptionalProperties({
		client: isNullable(isString),
		relatedDocRef: isNullable(isString)
	})
	.get();

export type InvitationDto = GuardedType<typeof isInvitationDto>;

export const isInvitationDtoArray = isArray(isInvitationDto);

export interface CreateInvitationRequest {
	expiresIn: ExpiresIn;
	recipients: { email: string }[];
	client?: string | null;
}

export interface CreateInvitationResponse {
	created: InvitationDto[];
	skipped: { email: string; reason: string }[];
}

export const isCreateInvitationResponse = new IsInterface()
	.withProperties({
		created: isInvitationDtoArray,
		skipped: isArray(new IsInterface().withProperties({ email: isString, reason: isString }).get())
	})
	.get();

export const isRecipientEntry = new IsInterface().withOptionalProperties({ email: isNullable(isString) }).get();
export type RecipientEntry = GuardedType<typeof isRecipientEntry>;

export const isExistingInvitationEntry = new IsInterface()
	.withProperties({ email: isString, token: isString })
	.withOptionalProperties({
		status: isSingletonStringUnion("ACTIVE", "USED", "REVOKED", "EXPIRED"),
		createdAt: isStringOrDate,
		expiresAt: isStringOrDate,
		relatedDocRef: isNullable(isString)
	})
	.get();
export type ExistingInvitationEntry = GuardedType<typeof isExistingInvitationEntry>;
export const isExistingInvitationEntryArray = isArray(isExistingInvitationEntry);

export const isExpiresIn = isSingletonStringUnion("HOURS_24", "HOURS_48", "HOURS_72", "DAYS_7", "DAYS_14", "DAYS_30");
export type ExpiresIn = GuardedType<typeof isExpiresIn>;

export const isNewInvitationsData = new IsInterface()
	.withProperties({
		expiresIn: isExpiresIn
	})
	.withOptionalProperties({
		recipients: isArray(isRecipientEntry),
		client: isNullable(isString),
		clientPrefilled: isBoolean,
		emptyClientAllowed: isBoolean,
		contactsAvailable: isBoolean
	})
	.get();
export type NewInvitationsData = GuardedType<typeof isNewInvitationsData>;

export const isInvitationDocumentRoot = new IsInterface()
	.withProperties({
		newInvitations: isNewInvitationsData,
		existingInvitations: isExistingInvitationEntryArray
	})
	.get();
export type InvitationDocumentRoot = GuardedType<typeof isInvitationDocumentRoot>;

export function invitationDtoToEntry(dto: InvitationDto): ExistingInvitationEntry {
	return {
		email: dto.email,
		token: dto.tokenHash,
		status: dto.status,
		createdAt: dto.createdAt ? new Date(dto.createdAt) : undefined,
		expiresAt: dto.expiresAt ? new Date(dto.expiresAt) : undefined,
		relatedDocRef: dto.relatedDocRef
	};
}

export const isInvitationDocumentData = new IsInterface()
	.withProperties({ document: new IsInterface().withProperties({ root: isInvitationDocumentRoot }).get() })
	.get();
export type InvitationDocumentData = GuardedType<typeof isInvitationDocumentData>;

export const isInvitationConfirmModalData = new IsInterface()
	.withProperties({
		title: isString,
		message: isString,
		buttonLabel: isString
	})
	.get();

export type InvitationConfirmModalData = GuardedType<typeof isInvitationConfirmModalData>;
